// v1.1.25 — the Swap screen (the home redesign, decided 3–4 Oct; mock-ups
// final 4 Oct 16:17). Laid out like the home: the two amounts on one row with
// Alexander's semibold arrow between them (no container; → / ← flips the
// direction, the source side follows the arrow), each with a currency pill
// under it (code only); the keypad; the bottom row = ✕ squircle + Slide to
// swap. The source amount is M white / black, the converted one green /
// indigo; past the pocket both grey, the balance pill turns amber and (1.4.86)
// Add money's glyph shows on the source line (the dock has no Add since 1.4.85). Swap is needed only to lock in a rate —
// normally conversion is automatic.
//
// The pricing and the swap itself are FxSource's (quote / swap), exactly as
// the Swap sheet used them (fxSheet.ts, which keeps the currency list).

import { CURRENCIES, currency, formatFx, liveCurrencies, rateDecimals, type FxPrepared, type FxQuote, type FxSource } from "./fx";
import { ARROW_R, IC_ARROW_DOWN, IC_ARROW_UP } from "./icons";
// 1.4.95 (Alexander, 24 Oct) — the currency picker's glyph: ⌃⌄ beside the code (a chevron down until then)
const IC_UPDOWN = `<svg viewBox="0 0 10 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1.5 6l3.5-3.5L8.5 6M1.5 10l3.5 3.5L8.5 10"/></svg>`;
import { initSlideToConfirm } from "./slideToConfirm";
import { recordError, failureLine } from "./diag";
import { LOGO_M } from "./logo";
import { playDone } from "./sound";

const IC_CLOSE = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>`;
const IC_DEL = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round" stroke-linecap="round" aria-hidden="true"><path d="M9 5h10.5A1.5 1.5 0 0 1 21 6.5v11a1.5 1.5 0 0 1-1.5 1.5H9l-6-7z"/><path d="M12 9.5l5 5"/><path d="M17 9.5l-5 5"/></svg>`;

const esc = (s: string) =>
  s.replace(/[&<>"']/g, (c) => (c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : c === '"' ? "&quot;" : "&#39;"));

export interface SwapScreenDeps {
  /** The pocket the swap starts from (the active one). */
  from: string;
  source: FxSource;
  /** Cash in a pocket (0 for pockets that can't hold money yet). */
  balanceOf: (code: string) => number;
  /** ✕ on the success card, or leaving Swap. */
  onDone: () => void;
  /** v1.1.31 — ✕: back to the main screen, the globe still lit. */
  onClose: () => void;
  /** The source pocket changed: the balance pill shows it. */
  onSource: (code: string) => void;
  /** Past the source pocket (true) or back within it (false). */
  onOver: (over: boolean) => void;
  /** 1.4.86 — the Add on the source line, shown while the amount is past the pocket: Add money (a sheet over Swap). */
  onAddMoney?: () => void;
  toast: (msg: string) => void;
  explorerUrl: (txHash: string) => string;
}

export interface SwapScreenHandle {
  close: () => void;
}

export function renderSwapScreen(host: HTMLElement, deps: SwapScreenDeps): SwapScreenHandle {
  const { source } = deps;
  let lC = deps.from;
  let rC = lC === "USD" ? "EUR" : "USD";
  let right = true; // → : left is the source
  const srcC = () => (right ? lC : rC);
  const dstC = () => (right ? rC : lC);
  let typed = "";
  // v1.1.32 — which amount the keypad writes: the source (default) or the
  // target; a tap on an amount picks it. The other side follows the rate.
  let edit: "src" | "dst" = "src";
  let unitRate = 0; // units of the target per one unit of the source
  let touched = false;
  let over = false;
  // 1.5.13 (v2.2; 25 Oct: the euro pool at €0.01, every swap into euros refused at the slide as buffer_short) —
  // M's pool for the target can't pay this amount: line 2 says so and the slide stays off, so the slide can't fail.
  let poolShort = false;
  let shortKey = ""; // …and the amount the Worker refused as buffer_short at the slide: held while the amount stands, even when the pool is unknown
  let quote: FxQuote | null = null;
  let timer: number | undefined;
  // 1.4.21 (Alexander, 16 Oct) — the rate moved against the person between the
  // screen and the slide: line 2 says so in the alert colour, where the rate
  // was (1.4.20 and before: a toast). Cleared by the next key, direction or
  // currency; a fresh slide swaps at the new figure.
  let rateAlert: string | null = null;
  let alertOut: number | null = null; // 1.4.24 — the figure the alert names
  let alertAt = 0; // …and when it was raised (the re-quote straight after doesn't clear it; a later poll may)
  // 1.4.25 (Alexander, 16 Oct: option (a)) — the swap is made ready before the
  // slide, as Send's payment is (v0.90.29): once the amount has rested
  // PREP_AFTER_MS the Worker signs a voucher and the userOp is prepared, and the
  // figure on screen IS the signed one. The slide then goes straight to Face ID
  // (iOS no longer asks "Use Passkey" first — the passkey request now follows
  // the finger) and there is no rate to move. Renewed before it runs out; any
  // change drops it. Nothing prepared → the slide takes the 1.4.21 path.
  // v2.0 (22 Oct, the Gas Station's cap) — the userOp is prepared with Circle's
  // stub only and sponsored on touch (source.touch); the renewals are capped at
  // RENEW_MAX for one amount (~4.5 min), after which the slide takes the plain path.
  const PREP_AFTER_MS = 600;
  const RENEW_MAX = 5;
  let renewals = 0;
  let renewKey = ""; // the amount the renewals are counted for
  let prep: FxPrepared | null = null;
  let prepKey = "";
  let prepTimer: number | undefined;
  let renewTimer: number | undefined;
  const keyNow = () => `${srcC()}>${dstC()}:${srcAmount().toFixed(6)}`;
  const prepValid = () => !!prep && prepKey === keyNow() && Date.now() < prep.usableUntil;
  function dropPrep() {
    prep = null;
    prepKey = "";
    window.clearTimeout(prepTimer);
    window.clearTimeout(renewTimer);
  }
  function paintRate(text: string, cls: "" | "note" | "alert") {
    const el = $("#swRate");
    el.textContent = rateAlert ?? text;
    el.classList.toggle("note", !rateAlert && cls === "note");
    el.classList.toggle("alert", !!rateAlert || cls === "alert");
    // 1.4.29 — line 2 is one line: the longest words ("South African rand open soon.",
    // "Rate update: now ₩13,700") shrink to fit rather than wrap.
    el.style.fontSize = "";
    const card = el.previousElementSibling?.getBoundingClientRect(); // 1.4.95 — the line under the card: the card's width less a margin
    const maxW = Math.max(300, (card?.width ?? 0) - 32);
    for (let fs = 13; fs > 10 && el.scrollWidth > maxW; fs -= 0.5) el.style.fontSize = `${fs - 0.5}px`;
  }

  host.innerHTML = `
    <div class="sw25">
      <!-- 1.4.15 (Alexander, 16 Oct) — three lines: the currency pill over the keypad's left column with
           its amount centred in the rest of the line; the direction arrow under the pill; the other
           currency the same way. The pill's text takes its amount's colour (the source white, the
           target green / indigo), so it follows the arrow. (1.1.25–1.4.14: the two amounts on one row
           with the arrow between them, a pill under each.) -->
      <div class="sw-stage">
        <!-- 1.4.86 (Alexander, 22 Oct) — while the amount passes the pocket, Add money's glyph on the source line, in the
             ✕'s column on line 2 (the home says "Low balance. Add money?"; Swap has no line for it). -->
        <!-- 1.4.95 (Alexander, 24 Oct) — the two lines in a card: the figures right-aligned, the code with a ⌃⌄ picker
             before each (the symbol gone — the code says it); the arrow alone on a divider between them (1.4.16: the
             rate beside it, and a second ✕ — gone: the ✕ by the slider is the way out). Add money, past the pocket,
             sits at the line's left. The rate stands under the card. -->
        <div class="sw-line" id="swLLine"><button type="button" class="sw-ccy" data-pick="l"><span id="swLCode"></span>${IC_UPDOWN}</button><div class="sw-amt" id="swLAmt"></div></div>
        <div class="sw-line sw-mid"><button type="button" class="sw-dir" id="swDir" aria-label="Change direction"></button></div>
        <div class="sw-line" id="swRLine"><button type="button" class="sw-ccy" data-pick="r"><span id="swRCode"></span>${IC_UPDOWN}</button><div class="sw-amt" id="swRAmt"></div></div>
      </div>
      <p class="sw-rate" id="swRate"></p>
      <div class="kp21 rest" id="swPad">
        ${["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((k) => `<button type="button" class="k21" data-k="${k}">${k}</button>`).join("")}
        <button type="button" class="k21 bare" data-k="." aria-label="Point">·</button><button type="button" class="k21" data-k="0">0</button><button type="button" class="k21 bare" data-k="back" aria-label="Delete">${IC_DEL}</button>
      </div>
      <!-- 1.5.4 (Alexander, 24 Oct) — past the pocket, a line under the keypad in the home's serif: "Low balance." in the
           alert colour, "Add money?" in the action green, and that phrase is the Add money button (1.4.86–1.5.3: Add
           money's glyph on the source line). Placed between the keys and the dock by measurement (placeLow). -->
      <p class="sw-low" id="swLow" hidden><span class="w">Low balance.</span> <button type="button" class="line21-act" id="swLowAdd">Add money?</button></p>
      <div class="act21 sw-act">
        <button id="swX" class="x21 in" type="button" aria-label="Back to your wallet">${IC_CLOSE}</button>
        <div class="slide off" id="swSlide" role="button" aria-label="Swap">
          <div class="fill"></div>
          <div class="lbl">Swap</div>
          <div class="ghost">${ARROW_R}</div>
          <div class="thumb">${ARROW_R}</div>
        </div>
      </div>
      <div class="sw-veil" id="swVeil" hidden></div>
      <div class="sw-list" id="swList" role="dialog" aria-label="Currency" hidden></div>
    </div>`;

  const $ = <T extends HTMLElement>(sel: string) => host.querySelector<T>(sel)!;
  const value = () => Number(typed || "0");
  /** What leaves the source pocket: the typed amount, or the target amount converted back. */
  const srcAmount = () => (edit === "src" ? value() : unitRate > 0 ? Math.ceil((value() / unitRate) * 100) / 100 : 0);
  const avail = () => Math.floor(deps.balanceOf(srcC()) * 100) / 100;
  const shown = (t: string) => {
    if (!t) return "0";
    const [i, d] = t.split(".");
    return Number(i || "0").toLocaleString("en-US") + (d != null ? `.${d}` : "");
  };
  /** 1.5.2 — the point and what follows at 60 % (CSS: .dec), the text otherwise exactly as written ("12." stays "12."). */
  const amtHtml = (code: string, text: string) => {
    const i = text.indexOf(".");
    const num = i < 0 ? esc(text) : `${esc(text.slice(0, i))}<span class="dec">${esc(text.slice(i))}</span>`;
    return `<span class="numw"><span class="cur">${esc(currency(code).symbol.trim())}</span><span class="num">${num}</span></span>`;
  };
  /** 1.5.2 (Alexander) — the figure's size steps down at 1,000, 10,000 and 100,000 (and 1,000,000), never at every tap. */
  const sizeStep = (text: string) => {
    const ints = (text.split(".")[0] ?? "").replace(/\D/g, "").replace(/^0+(?=\d)/, "").length;
    return ints <= 3 ? 0 : ints - 3 > 4 ? 4 : ints - 3;
  };
  /** …each step's size fitted once, against the step's widest figure (9,999.99 for the step from 1,000) with this line's
   *  code: the CSS sizes are ceilings, and the size the step takes keeps the code right of the divider in whatever font
   *  the phone has (GitHub's runner's font is wider than the workspace's; the phone's is neither). Within a step the size
   *  never changes, whatever is typed. Cached per code, step, decimals, line width and ceiling. */
  const fitted = new Map<string, number>();
  function fitLine(line: HTMLElement, code: string, text: string) {
    const amt = line.querySelector<HTMLElement>(".sw-amt"), num = amt?.querySelector<HTMLElement>(".num"), ccy = line.querySelector<HTMLElement>(".sw-ccy");
    if (!amt || !num || !ccy) return;
    num.style.fontSize = "";
    const step = Number(amt.dataset.sz ?? "0"), ceil = parseFloat(getComputedStyle(num).fontSize);
    const ints = Math.max(step + 3, (text.split(".")[0] ?? "").replace(/\D/g, "").length); // step 4: the figure's own digits
    const decs = Math.max(currency(code).dp, text.includes(".") ? text.split(".")[1].length : 0);
    const box = line.getBoundingClientRect();
    const key = `${code}|${ints}|${decs}|${Math.round(box.width)}|${ceil}`;
    let fs = fitted.get(key);
    if (fs == null) {
      const real = num.innerHTML, limit = box.left + 44; // the divider starts 44 px in (the arrow's column)
      const widest = Number("9".repeat(ints)).toLocaleString("en-US");
      num.innerHTML = decs ? `${widest}<span class="dec">.${"9".repeat(decs)}</span>` : widest;
      for (fs = ceil; fs > 16 && ccy.getBoundingClientRect().left < limit - 0.5; fs -= 1) num.style.fontSize = `${fs - 1}px`;
      num.innerHTML = real;
      fitted.set(key, fs);
    }
    num.style.fontSize = fs < ceil ? `${fs}px` : "";
  }

  /** 1.5.4 — "Low balance. Add money?" under the keypad while the amount is past the pocket, and nowhere else
   *  (1.4.86–1.5.3: Add money's glyph on the source line). Without an Add money route, "Low balance." alone. */
  function paintAdd() {
    $("#swLowAdd").hidden = !deps.onAddMoney;
    $("#swLow").hidden = !over;
    if (over) placeLow();
  }
  /** Centred between the keypad's last row and the dock (the keypad is hung from the bottom and shifted; measured). */
  function placeLow() {
    const low = $("#swLow");
    const kb = $("#swPad").getBoundingClientRect().bottom;
    const dock = document.querySelector<HTMLElement>(".dock-inner")?.getBoundingClientRect();
    const dt = dock && dock.height ? dock.top : innerHeight - 90;
    low.style.top = `${Math.round((kb + dt) / 2 - low.offsetHeight / 2)}px`;
  }
  const onResize = () => { if (!host.isConnected) { removeEventListener("resize", onResize); return; } if (!$("#swLow").hidden) placeLow(); };
  addEventListener("resize", onResize);
  function paint() {
    paintAdd();
    const has = typed.length > 0;
    const fmtN = (code: string, n: number) => n.toLocaleString("en-US", { minimumFractionDigits: currency(code).dp, maximumFractionDigits: currency(code).dp });
    const srcText = edit === "src" ? shown(typed) : quote && has ? fmtN(srcC(), quote.amountIn) : has && unitRate > 0 ? fmtN(srcC(), srcAmount()) : "0";
    const dstText = edit === "dst" ? shown(typed) : quote && has ? fmtN(dstC(), quote.amountOut) : "0";
    const lText = right ? srcText : dstText, rText = right ? dstText : srcText;
    $("#swLAmt").innerHTML = amtHtml(lC, lText);
    $("#swRAmt").innerHTML = amtHtml(rC, rText);
    $("#swLAmt").dataset.sz = String(sizeStep(lText));
    $("#swRAmt").dataset.sz = String(sizeStep(rText));
    const lRole = right ? "src" : "dst", rRole = right ? "dst" : "src";
    $("#swLAmt").className = `sw-amt ${lRole}${over ? " over" : ""}${edit === lRole ? " edit" : ""}`;
    $("#swRAmt").className = `sw-amt ${rRole}${over ? " over" : ""}${edit === rRole ? " edit" : ""}`;
    $("#swLLine").className = `sw-line ${lRole}`;
    $("#swRLine").className = `sw-line ${rRole}`;
    $("#swLCode").textContent = lC;
    $("#swRCode").textContent = rC;
    $("#swDir").innerHTML = right ? IC_ARROW_DOWN : IC_ARROW_UP; // 1.4.16: vertical
    fitLine($("#swLLine"), lC, lText); // after the codes are written: the fit measures this line's own code
    fitLine($("#swRLine"), rC, rText);
    const glass = touched || has;
    $("#swPad").classList.toggle("glass", glass);
    $("#swPad").classList.toggle("rest", !glass);
    const ready = !!quote && has && !over && !poolShort && srcAmount() > 0;
    $("#swSlide").classList.toggle("off", !ready);
  }

  // 1.4.95 (Alexander, 24 Oct) — the rate in codes, under the card: "1 USD ≈ 0.8500 EUR" (1.4.30–1.4.94: "$1 = €0.8500"
  // beside the arrow). ≈ because it moves; the precision is ECB-style (fx.ts rateDecimals).
  const rateLine = (from: string, to: string, rate: number) => { const dp = rateDecimals(rate); return `1 ${from} ≈ ${rate.toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp })} ${to}`; };
  /** "Swiss francs", "South African rand", "Korean won" — the plural a person says. */
  const poolName = (code: string) => {
    const n = currency(code).name.replace(/^South Korean /, "Korean ");
    if (/krona$/i.test(n)) return n.replace(/krona$/i, "kronor");
    return /(rand|won)$/i.test(n) ? n : n.replace(/(peso|real|dollar|franc|pound|euro)$/i, (m) => (m.toLowerCase() === "real" ? "reais" : m + "s"));
  };
  /** 1.5.13 — "Not enough euros on M's exchange right now." */
  const shortLine = (to: string) => `Not enough ${poolName(to).replace(/^Euros$/, "euros")} on M's exchange right now.`;
  const prepRateText = (p: FxPrepared) => rateLine(p.quote.from, p.quote.to, p.amountOut / p.quote.amountIn);
  async function refreshQuote() {
    const from = srcC(), to = dstC();
    if (prepValid()) { quote = prep!.quote; paintRate(prepRateText(prep!), ""); paint(); return; } // 1.4.25 — the signed figure holds
    // 1.4.29 — a currency whose pool M hasn't stocked yet: say so, keep the slide off.
    const pool = source.poolOf ? await source.poolOf(to) : null;
    if (from !== srcC() || to !== dstC()) return;
    if (pool !== null && pool <= 0) {
      quote = null;
      poolShort = false;
      paintRate(`${poolName(to)[0].toUpperCase()}${poolName(to).slice(1)} open soon.`, "note");
      paint();
      return;
    }
    try {
      const unit = await source.quote(from, to, 1);
      if (from !== srcC() || to !== dstC()) return; // changed while we asked
      paintRate(rateLine(from, to, unit.rate), "");
      unitRate = unit.rate;
      over = srcAmount() > avail() + 1e-9; // the target amount's source side is known now
      deps.onOver(over);
      paintAdd();
      quote = srcAmount() > 0 ? await source.quote(from, to, srcAmount()) : null;
      // 1.4.24 — a newer rate replaces the alert's figure: the alert goes, so
      // line 2 and the amount never disagree (16 Oct: "now €5.30" over €5.33).
      if (rateAlert && quote && alertOut != null && Date.now() - alertAt > 2_000 && Math.abs(quote.amountOut - alertOut) >= 0.005) {
        rateAlert = null;
        alertOut = null;
        paintRate(rateLine(from, to, unit.rate), "");
      }
      // 1.5.13 — the pool short of this amount: say so on line 2, no voucher asked for, the slide off.
      poolShort = (pool !== null && !!quote && quote.amountOut > pool + 1e-9) || shortKey === keyNow();
      if (poolShort && !rateAlert) paintRate(shortLine(to), "note");
      if (quote && !over && !poolShort && !prepValid()) schedulePrep();
    } catch {
      quote = null;
      poolShort = false;
      const off = [from, to].find((c) => !liveCurrencies().includes(c));
      // v1.1.29 — it names the pocket that can't swap yet, whichever way the arrow points.
      paintRate(off ? `Swaps with ${off} aren't open yet.` : "Couldn't load a rate just now.", "note");
    }
    paint();
  }
  function schedulePrep() {
    if (keyNow() === renewKey && renewals >= RENEW_MAX) return; // this amount's are spent (the rate poll asks too): the slide's plain path
    window.clearTimeout(prepTimer);
    prepTimer = window.setTimeout(() => void doPrep(), PREP_AFTER_MS);
  }
  async function doPrep() {
    if (!source.prepareSwap || !quote || over || swapping() || document.hidden) return;
    if (source.canPrepare && !(await source.canPrepare())) return;
    const key = keyNow();
    const q: FxQuote = { ...quote, amountIn: srcAmount() };
    try {
      const p = await source.prepareSwap(q);
      if (key !== keyNow() || swapping()) return; // changed (or slid) while it was being made
      prep = p;
      prepKey = key;
      if (key === renewKey) renewals++; else { renewKey = key; renewals = 0; } // v2.0 — counted per voucher made, per amount
      quote = p.quote;
      paintRate(prepRateText(p), "");
      paint();
      window.clearTimeout(renewTimer);
      renewTimer = window.setTimeout(() => { if (prepKey === key) { prep = null; schedulePrep(); } }, Math.max(1_000, p.usableUntil - Date.now()));
    } catch {
      /* not prepared: the slide takes the plain path, which says what went wrong */
    }
  }
  const scheduleQuote = () => { window.clearTimeout(timer); timer = window.setTimeout(() => void refreshQuote(), 180); };

  function setTyped(t: string) {
    dropPrep();
    rateAlert = null;
    alertOut = null;
    typed = t;
    over = srcAmount() > avail() + 1e-9;
    deps.onOver(over);
    paintAdd();
    quote = null;
    poolShort = false;
    paint();
    scheduleQuote();
  }

  $("#swPad").addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>("button");
    if (!b) return;
    const k = b.dataset.k!;
    touched = true;
    if (k === "back") { const n = typed.slice(0, -1); setTyped(n === "0" ? "" : n); return; }
    if (over) return; // nothing more past the pocket (the home's rule)
    if (k === ".") { if (!typed.includes(".")) setTyped((typed || "0") + "."); return; }
    if (/\.\d\d$/.test(typed)) return;
    setTyped((typed === "0" ? "" : typed) + k);
  });
  $("#swX").addEventListener("click", () => deps.onClose()); // v1.1.31 — back to the home, Globe on
  $("#swLowAdd").addEventListener("click", () => deps.onAddMoney?.()); // 1.5.4 (1.4.86: the glyph)
  $("#swDir").addEventListener("click", () => {
    right = !right;
    edit = "src";
    deps.onSource(srcC());
    setTyped("");
  });
  // v1.1.32 — a tap on either amount: the keypad writes that one.
  for (const [id, side] of [["#swLAmt", "l"], ["#swRAmt", "r"]] as const) {
    $(id).addEventListener("click", () => {
      const role: "src" | "dst" = (side === "l") === right ? "src" : "dst";
      if (role === edit) return;
      edit = role;
      touched = true;
      setTyped("");
    });
  }

  // The currency list: the current one first (accent), every other currency
  // alphabetically by full name. Picking the other side's currency swaps them.
  function closeList() {
    $("#swVeil").hidden = true;
    $("#swList").hidden = true;
  }
  function openList(side: "l" | "r") {
    const now = side === "l" ? lC : rC;
    const rest = CURRENCIES.filter((c) => c.code !== now).sort((a, b) => a.name.localeCompare(b.name));
    const row = (code: string, name: string, on: boolean) =>
      `<button type="button" class="opt${on ? " on" : ""}" data-code="${code}"><b>${code}</b><span>${esc(name)}</span></button>`;
    $("#swList").innerHTML = `
      <div class="lh"><button type="button" class="lx" id="swListX" aria-label="Close">${IC_CLOSE}</button>Currency</div>
      <div class="rows">${row(now, currency(now).name, true)}${rest.map((c) => row(c.code, c.name, false)).join("")}</div>`;
    $("#swVeil").hidden = false;
    $("#swList").hidden = false;
    $("#swListX").addEventListener("click", closeList);
    $("#swList").querySelectorAll<HTMLButtonElement>(".opt").forEach((o) =>
      o.addEventListener("click", () => {
        const code = o.dataset.code!;
        const other = side === "l" ? rC : lC;
        if (code === other) { const t = lC; lC = rC; rC = t; }
        else if (side === "l") lC = code;
        else rC = code;
        closeList();
        edit = "src";
        deps.onSource(srcC());
        setTyped("");
      }),
    );
  }
  $("#swVeil").addEventListener("click", closeList);
  host.querySelectorAll<HTMLButtonElement>(".sw-ccy").forEach((b) => b.addEventListener("click", () => openList(b.dataset.pick as "l" | "r")));

  // The confirming overlay and the success card, as the Swap sheet had them.
  document.getElementById("fxOverlay")?.remove();
  const overlay = document.createElement("div");
  overlay.id = "fxOverlay";
  overlay.className = "drop-overlay";
  overlay.setAttribute("hidden", "");
  document.body.appendChild(overlay);
  const hideOverlay = () => { overlay.setAttribute("hidden", ""); overlay.innerHTML = ""; };
  function overlaySpinner(q: FxQuote) {
    overlay.innerHTML = `<div class="do-body"><div class="spinner big"></div><div class="do-h">Swapping ${esc(formatFx(q.from, q.amountIn))}…</div><div class="do-s" id="fxDoStage">Locking the rate…</div></div>`;
    overlay.removeAttribute("hidden");
  }
  function overlaySuccess(q: FxQuote, got: number, txHash: string) {
    playDone(); // 1.5.10 — M's "done": a completion has landed
    overlay.innerHTML = `
      <div class="do-stack">
        <div class="brand small">${LOGO_M}</div>
        <div class="do-card">
          <button id="fxDoClose" class="do-close" type="button" aria-label="Close">✕</button>
          <div class="tickbig">✓</div>
          <div class="do-h one"><span class="amt">${esc(formatFx(q.to, got))}</span> <span class="nm">from ${esc(formatFx(q.from, q.amountIn))}</span></div>
          <a class="do-link" href="${esc(deps.explorerUrl(txHash))}" target="_blank" rel="noopener">See receipt on Circle Arc ↗</a>
        </div>
      </div>`;
    overlay.querySelector("#fxDoClose")!.addEventListener("click", () => { overlay.remove(); deps.onDone(); });
  }
  // 1.4.24 (Alexander, 16 Oct) — one line, no reason, just Back. The reason
  // (a cancelled Face ID, a Worker refusal) goes to Lab via recordError; the
  // ✕ that overlapped the title is gone. The title is fitted to one line.
  /** 1.5.13 — the line under the title names the cause when it is known, as the payment's failure screen does since
   *  1.4.77 (until now: the title alone; the reason was in Lab only). */
  function overlayError(why: string) {
    overlay.innerHTML = `
      <div class="do-body"><div class="do-h sw-fail">Swap couldn’t be completed.</div><div class="do-s sw-why">${esc(why)}</div><button id="fxDoBack" class="btn ghost" type="button" style="margin-top:14px">Back</button></div>`;
    const h = overlay.querySelector<HTMLElement>(".sw-fail")!;
    const avail = overlay.clientWidth - 32;
    for (let fs = 28; fs > 14 && h.scrollWidth > avail; fs--) h.style.fontSize = `${fs - 1}px`;
    const back = () => { hideOverlay(); slider.reset(); };
    overlay.querySelector("#fxDoBack")!.addEventListener("click", back);
  }

  /** 1.5.13 — what the failure screen says. A Worker refusal (FxRefused) comes before anything is signed; a pool
   *  found short at the slide names the currency; a receipt that never came is not "nothing moved". */
  function swapWhy(e: unknown): string {
    const err = e as { name?: string; code?: string; message?: string };
    if (err?.name === "FxRefused") {
      if (err.code === "buffer_short") {
        if (srcAmount() > 0) { poolShort = true; shortKey = keyNow(); paintRate(shortLine(dstC()), "note"); paint(); }
        return `${shortLine(dstC())} Nothing moved.`;
      }
      return `${(err.message || "Couldn't get a rate just now.").replace(/\.?$/, ".")} Nothing moved.`;
    }
    if (/userop_receipt_timeout/.test(`${err?.message ?? ""} ${String((e as { cause?: unknown })?.cause ?? "")}`)) {
      return "Circle Arc hasn't confirmed it yet. Check Recent transactions before trying again.";
    }
    return failureLine(e);
  }

  const slider = initSlideToConfirm($("#swSlide"), {
    onTouch: () => { if (prepValid() && source.touch) source.touch(prep!); }, // v2.0 — sponsor on touch
    // 1.5.33 — the ✕ in the button's left segment (Swap's own ✕, as the dock's ✕ did)
    onX: () => $("#swX").click(),
    onConfirm: () => {
      if (!quote || over) { slider.reset(); return; }
      if (!source.live) { deps.toast("Swapping opens soon"); slider.reset(); return; }
      const ready = prepValid() && source.sendSwap ? prep! : null;
      const q = ready ? ready.quote : quote;
      window.clearTimeout(renewTimer);
      overlaySpinner(q);
      const onStage = () => { const st = overlay.querySelector("#fxDoStage"); if (st) st.textContent = "Confirming on Circle Arc"; };
      void (ready ? source.sendSwap!(ready, onStage) : source.swap(q, onStage))
        .finally(() => dropPrep()) // a voucher is used once
        .then((r) => overlaySuccess(q, r?.amountOut ?? q.amountOut, r.txHash))
        .catch((e: unknown) => {
          const err = e as { message?: string; amountOut?: number };
          if (typeof err?.amountOut === "number") {
            // 1.4.21 — the price moved against the person: nothing was signed.
            // The new figure on line 2 in the alert colour; slide again to take it.
            hideOverlay();
            rateAlert = `Rate update: now ${formatFx(dstC(), err.amountOut)}`;
            alertOut = err.amountOut;
            alertAt = Date.now();
            paintRate("", "alert");
            void refreshQuote();
            slider.reset();
          } else {
            recordError("swap", e);
            overlayError(swapWhy(e));
          }
        });
    },
  });

  deps.onSource(srcC());
  paint();
  void refreshQuote();
  // 1.4.22 (Alexander's video, 16 Oct: the screen showed $1 = €0.9071 / €5.44,
  // the voucher priced €5.32) — the rate is polled while the screen is open, as
  // the Swap sheet's was (lost in the 1.1.25 rewrite: the screen asked only on
  // open and on a key, so a screen left open kept an old rate). Every
  // RATE_POLL_MS while visible and nothing is swapping, and on coming back to
  // the tab. MFxSource caches 4 s, so this is one /fx/rate per poll.
  const RATE_POLL_MS = 8_000;
  const swapping = () => !overlay.hasAttribute("hidden");
  const poll = window.setInterval(() => { if (!document.hidden && !swapping()) void refreshQuote(); }, RATE_POLL_MS);
  const onVis = () => { if (!document.hidden && !swapping()) void refreshQuote(); };
  document.addEventListener("visibilitychange", onVis);

  return {
    close: () => {
      window.clearInterval(poll);
      dropPrep();
      document.removeEventListener("visibilitychange", onVis);
      window.clearTimeout(timer);
      overlay.remove();
      deps.onOver(false);
      host.innerHTML = "";
    },
  };
}
